import React, { useEffect, useMemo, useState } from 'react'
import { Select } from '@/core/components/select'
import { OptionSelect } from '@/core/utils/types'
import { isArrayofStrings } from '@/core/utils/is-array-of-strings'
import { Hint } from '@tecsinapse/cortex-react'
import { ErrorMessage } from '@hookform/error-message'
import { useController, useFormContext } from 'react-hook-form'
import { GrupoUsuariosFavoritoOption } from './form-multi-select-favorito'

export interface FormMultiSelectProps {
  name: string
  label?: string
  options: OptionSelect[] | GrupoUsuariosFavoritoOption[]
  customOnChange?: (e: OptionSelect[] | GrupoUsuariosFavoritoOption[]) => void
  disabled?: boolean
  id?: string
  withSearch?: boolean
  className?: string
}

export const FormMultiSelect = ({
  name,
  label,
  options: _options = [],
  customOnChange,
  disabled,
  id,
  withSearch = false,
  className
}: FormMultiSelectProps) => {
  const { setValue, control } = useFormContext()
  const {
    field: { value = [], onBlur },
    formState,
    fieldState
  } = useController({ name, control })

  const errors = formState?.errors ?? {}
  const error = fieldState?.error ?? []
  const submitCount = formState?.submitCount

  const [searchText, setSearchText] = useState('')

  const labelExtractor = (option: OptionSelect) => option.label
  const keyExtractor = (option: OptionSelect) => option.value

  const filteredOptions: OptionSelect[] = (_options ?? []).filter(
    (option: OptionSelect) =>
      labelExtractor(option)?.toLowerCase().includes(searchText.toLowerCase())
  )
  const formatValue = isArrayofStrings(value)
    ? ((_options as OptionSelect[]) ?? []).filter(op =>
        (value as unknown as []).find(item => {
          return item === op?.value
        })
      )
    : (((_options as OptionSelect[]) ?? []).filter(op =>
        (value as OptionSelect[])?.find(item => item?.value === op?.value)
      ) ?? [])

  useEffect(() => {
    if (isArrayofStrings(value))
      setValue(name, formatValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      })
  }, [])

  const options: OptionSelect[] = useMemo(
    () => (withSearch ? filteredOptions : _options),
    [filteredOptions, _options, withSearch]
  )

  return (
    <div className={`w-full ${className}`}>
      <Select
        id={id}
        disabled={disabled}
        isMultiSelect
        options={options}
        label={label}
        value={formatValue ?? []}
        keyExtractor={keyExtractor}
        labelExtractor={labelExtractor}
        withSearch={withSearch}
        inputSearch={searchText}
        setInputSearch={setSearchText}
        onSelect={e => {
          setValue(name, e, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          })
          if (customOnChange) {
            customOnChange(e)
          }
        }}
        onBlur={onBlur}
      />
      {submitCount > 0 && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <Hint variants={{ intent: 'error' }}>{message}</Hint>
          )}
        />
      ) : null}
    </div>
  )
}
